import { FC } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Divider,
  ButtonBase,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { pxToRem } from "../../../util";
import { Colors } from "../../../configs";
import { useAppSelector } from "../../../hooks";
import UserAvatar from "../../common/user-avatar/UserAvatar";

type ActionListModalProps = {
  open: boolean;
  onClose: () => void;
  onLock: () => void;
  onLogout: () => void;
};

const ActionListModal: FC<ActionListModalProps> = ({
  open,
  onClose,
  onLock,
  onLogout,
}) => {
  const { user } = useAppSelector((state) => state.auth);

  const firstName = user?.firstName ?? "";
  const lastName = user?.lastName ?? "";
  const fullName = firstName
    ? `${firstName} ${lastName?.charAt(0)}.`
    : "";
  const roleName = user?.userRole?.name ?? user?.role?.name ?? "";
  const employeeId = user?.id ?? "";
  const initials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: pxToRem(320),
          maxWidth: "92vw",
          borderRadius: pxToRem(16),
          boxShadow:
            "0px 12px 40px rgba(0,0,0,0.15), 0px 2px 8px rgba(0,0,0,0.08)",
          overflow: "hidden",
          backgroundColor: Colors.WHITE,
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          "&:last-child": { pb: 0 },
          backgroundColor: Colors.WHITE,
        }}
      >
        {/* User Profile Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: pxToRem(12),
            px: pxToRem(20),
            pt: pxToRem(20),
            pb: pxToRem(16),
          }}
        >
          <UserAvatar
            src={user?.avatar}
            name={`${firstName} ${lastName}`}
            id={user?.id ?? 0}
            size={52}
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: pxToRem(3) }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: pxToRem(15),
                color: Colors.TEXT_GRAY_DARK,
                lineHeight: 1.2,
              }}
            >
              {fullName}
            </Typography>
            <Typography
              sx={{
                fontSize: pxToRem(12),
                color: "#999999",
                lineHeight: 1.3,
              }}
            >
              {roleName}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: pxToRem(5),
                mt: pxToRem(1),
              }}
            >
              <Box
                sx={{
                  width: pxToRem(7),
                  height: pxToRem(7),
                  borderRadius: "50%",
                  backgroundColor: "#22C55E",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: pxToRem(11),
                  color: "#999999",
                  lineHeight: 1,
                }}
              >
                Employee ID: {employeeId}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "#F0F0F0" }} />

        {/* Manage Account Heading + Actions */}
        <Box sx={{ px: pxToRem(20), pt: pxToRem(16), pb: pxToRem(20) }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: pxToRem(14),
              color: Colors.TEXT_GRAY_DARK,
              mb: pxToRem(12),
            }}
          >
            Manage Account
          </Typography>

          {/* Action Tiles Card */}
          <Box
            sx={{
              border: `1px solid #EBEBEB`,
              borderRadius: pxToRem(12),
              overflow: "hidden",
              backgroundColor: Colors.WHITE,
              mb: pxToRem(10),
            }}
          >
            {/* Lock Account */}
            <ButtonBase
              onClick={onLock}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: pxToRem(12),
                px: pxToRem(16),
                py: pxToRem(15),
                justifyContent: "flex-start",
                "&:hover": { backgroundColor: "#F9F9F9" },
                "&:active": { backgroundColor: "#F2F2F2" },
              }}
            >
              <LockOutlinedIcon
                sx={{ fontSize: pxToRem(19), color: "#555555" }}
              />
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: pxToRem(13),
                  color: Colors.TEXT_GRAY_DARK,
                }}
              >
                Lock Account
              </Typography>
            </ButtonBase>

          </Box>

          {/* Logout Button */}
          <ButtonBase
            onClick={onLogout}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: pxToRem(12),
              px: pxToRem(16),
              py: pxToRem(14),
              justifyContent: "flex-start",
              borderRadius: pxToRem(12),
              backgroundColor: "#FDECEA",
              "&:hover": { backgroundColor: "#FAD9D6" },
              "&:active": { backgroundColor: "#F5C8C4" },
            }}
          >
            <LogoutOutlinedIcon
              sx={{ fontSize: pxToRem(19), color: Colors.BROWN }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: pxToRem(13),
                color: Colors.BROWN,
              }}
            >
              Logout
            </Typography>
          </ButtonBase>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ActionListModal;
